/**
 * Wedding Books — Overview.
 *
 * Portfolio health at a glance over a chosen window: four KPI tiles with
 * sparklines and period-over-period deltas, who signed up, the invitation
 * funnel, which templates couples pick, how the emails are performing, the
 * status and cross-sell funnels, writing quality, the voice-clone quota, the
 * anniversary programme, reminder pressure, and a LIVE cross-book activity feed.
 *
 * ONE REQUEST STILL FEEDS THE WHOLE PAGE — but a section can now fail alone.
 * The server settles its aggregates individually: a query that throws ships its
 * own key as `null` and names itself in `unavailable[]`, and the other sections
 * come back exactly as they were. So this page no longer has a single error
 * state for eighteen numbers. It has one per card.
 *
 * ⚠️ `unavailable[]` AND `available: false` ARE DIFFERENT THINGS AND MUST LOOK
 * DIFFERENT.
 *   · `unavailable[]` — the query FAILED. A retry may well work, so that card
 *     gets an EmptyState with a Retry button wired to `load`.
 *   · `available: false` (anniversary, coordinators, the email summary) — the
 *     table is not on this database and no amount of retrying will put it
 *     there. That card explains itself in the server's words and offers no
 *     button. A Retry there would be a control that cannot possibly help,
 *     which teaches an operator to stop believing the other one.
 *
 * ⚠️ THE EmptyState CONTRACT IS `icon: <string name>` AND `action: {label,
 * onClick}`. Four sites in this section passed React elements to both and
 * produced blank buttons wired to nothing — on the one screen an operator
 * reaches only when something has already gone wrong. Do not copy an older
 * pattern from elsewhere in the section; copy this one.
 *
 * THE RANGE LIVES IN THE URL. `useTimeRangeParam` derives it from the query
 * string on every render, so a refresh, a back button and a pasted link all
 * land on the same window. The default is 30 days, matching the server's.
 *
 * LAST REQUEST WINS. `load` carries a monotonic sequence token, so a slow
 * response for an older range cannot land after a newer one and repaint the
 * page with numbers the filter no longer describes. The AICostsPage idiom.
 *
 * ⚠️ THE PROTOTYPE'S NUMBERS ARE ILLUSTRATIONS, NOT SEED DATA. Every widget on
 * this page has a real zero state, because a fresh install genuinely has none
 * of this, and a card that renders "0%" for something nothing measures is worse
 * than a card that says so.
 */

import React from 'react';
import {
  Box, Card, CardContent, Chip, Grid, Stack, Tooltip, Typography,
} from '@mui/material';
import { Favorite as WeddingIcon } from '@mui/icons-material';
import EmptyState from '../../common/EmptyState';
import TimeRangeFilter from '../../common/TimeRangeFilter';
import useTimeRangeParam from '../../common/useTimeRangeParam';
import { describeRange, toApiToken, resolveRangeYmd } from '../../common/timeRange';
import WeddingKpiCard from '../WeddingKpiCard';
import RangeMetaCaption from '../RangeMetaCaption';
import StatusFunnelCard from './StatusFunnelCard';
import ConversionFunnelCard from './ConversionFunnelCard';
import InvitationFunnelCard from './InvitationFunnelCard';
import TemplateUsageCard from './TemplateUsageCard';
import EmailTemplatesSummaryCard from './EmailTemplatesSummaryCard';
import NewestCouplesCard from './NewestCouplesCard';
import RecentActivityCard from './RecentActivityCard';
import WritingQualityCard from './WritingQualityCard';
import VoiceQuotaCard from './VoiceQuotaCard';
import AnniversaryProgrammeCard from './AnniversaryProgrammeCard';
import ReminderPressureCard from './ReminderPressureCard';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { ConversionFunnel, EngagementTotals, KpiBlock, WeddingOverview } from '../weddingApi';
import { centsToUsd, centsToUsdTile, num, percentage } from '../weddingFormat';

/**
 * Said the same way wherever an unmeasured provider metric appears, so an
 * operator who reads it once on this card recognises it on the guest table.
 */
const NOT_TRACKED_FALLBACK =
  'Not tracked. No per-guest open/bounce reconciliation has run against this database, so there is no '
  + 'number here rather than a zero that looks like one. An open is never inferred from a click.';

/**
 * The server decides whether these are measured and says why, in one sentence
 * it ships alongside the counts. Rendering its sentence rather than a constant
 * is what lets the card start telling the truth the moment the reconciliation
 * job runs — with no admin deploy, and with no second place to keep in sync.
 */
const trackingHelp = (engagement?: EngagementTotals) =>
  engagement?.tracking_reason || NOT_TRACKED_FALLBACK;

const EMPTY_KPI: KpiBlock = { value: null, prev: null, delta_pct: null };

/** A funnel with nothing in it — every rung really is zero, not unmeasured. */
const EMPTY_FUNNEL: ConversionFunnel = {
  people_touched: 0, people_clicked: 0, offered: 0, converted: 0,
  matched_existing: 0, converted_unlinked: 0, touched: 0, clicked: 0,
};

/** A KPI value that the server could not measure renders '—', never 0. */
function kpiCount(block: KpiBlock | undefined): string {
  if (!block || block.value === null || block.value === undefined) return '—';
  return num(block.value).toLocaleString();
}

/**
 * "new" is a claim, and an empty period has nothing to claim.
 *
 * `delta_pct: null` means the prior period was zero — which reads as "new" when
 * something happened this period and as noise when nothing did. Zero against
 * zero gets no chip at all rather than a badge announcing the arrival of
 * nothing.
 */
function deltaFor(block: KpiBlock): { deltaPct?: number | null; showDelta: boolean } {
  const measured = block.value !== null && block.value !== undefined && num(block.value) > 0;
  if (block.delta_pct === null && !measured) return { showDelta: false };
  return { deltaPct: block.delta_pct, showDelta: true };
}

/**
 * The card an operator sees where a section used to be when its query failed.
 *
 * Same height and border radius as the card it replaces, so the grid does not
 * jump — and a REAL Retry, using EmptyState's actual contract.
 */
const SectionFailure: React.FC<{ title: string; reason: string; onRetry: () => void }> = ({
  title, reason, onRetry,
}) => (
  <Card sx={{ height: '100%', borderRadius: '16px' }}>
    <CardContent>
      <EmptyState
        icon="error"
        title={title}
        description={reason}
        action={{ label: 'Retry', onClick: onRetry, variant: 'outlined' }}
      />
    </CardContent>
  </Card>
);

const WeddingOverviewPage: React.FC = () => {
  const [range, setRange] = useTimeRangeParam({ type: '30d' });

  const [data, setData] = React.useState<WeddingOverview | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const mountedRef = React.useRef(true);
  // Monotonic request token. Changing the range fires a new request while the
  // old one is still in flight; without this the slower of the two wins and the
  // page shows numbers for a window the filter no longer names.
  const reqSeq = React.useRef(0);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = React.useCallback(async () => {
    const seq = reqSeq.current + 1;
    reqSeq.current = seq;
    const stale = () => !mountedRef.current || reqSeq.current !== seq;

    setLoading(true);
    setError(null);
    try {
      // ALWAYS both halves of the wire: the token AND the resolved local calendar
      // dates, presets included. A bare token would let the server resolve its own
      // rolling millisecond window, so the same pill meant a different span here
      // than on Revenue — which sends explicit dates. The dates are the window;
      // the token only says which pill produced it.
      const ymd = resolveRangeYmd(range);
      const next = await weddingApi.getOverview({
        range: toApiToken(range), from: ymd.from, to: ymd.to,
      });
      if (stale()) return;
      setData(next);
    } catch (e) {
      if (stale()) return;
      setError(e instanceof WeddingApiError ? e.message : 'Could not load the wedding overview.');
    } finally {
      if (!stale()) setLoading(false);
    }
  }, [range]);

  React.useEffect(() => {
    load();
  }, [load]);

  const rangeLabel = describeRange(range);

  // Only a request that failed OUTRIGHT gets the full-page state now. A single
  // failed aggregate is a card, not a page.
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          icon="error"
          title="The wedding overview could not be loaded"
          description={error}
          action={{ label: 'Retry', onClick: load, variant: 'contained' }}
        />
      </Box>
    );
  }

  const books = data?.books;
  const revenue = data?.revenue;
  const participation = data?.participation;
  const conversions = data?.conversions;
  const engagement = data?.engagement;
  const kpis = data?.kpis;

  /**
   * The sections the server could not answer for on THIS request, and the
   * generic reason it gave. An absent array is the healthy state, not unknown:
   * a backend that predates O4 simply never fails a section on its own.
   */
  const unavailable = data?.unavailable || [];
  const failureFor = (key: string): string | null => {
    const hit = unavailable.find((u) => u && u.key === key);
    return hit ? hit.reason : null;
  };

  /** Render `node`, unless this section failed — then render the Retry card. */
  const section = (key: string, title: string, node: React.ReactNode): React.ReactNode => {
    const reason = failureFor(key);
    if (!reason) return node;
    return <SectionFailure title={title} reason={reason} onRetry={load} />;
  };

  /**
   * A SUBTITLE IS A SENTENCE, AND A SENTENCE ABOUT A FAILED SECTION IS A CLAIM.
   *
   * The tile VALUES already degrade to an em-dash when their own aggregate is
   * null, but each subtitle is built from a DIFFERENT section and reads it with
   * `?? 0`. So a failed `revenue` query printed "$0.00 gross, all time"
   * directly beneath an honest "—", and a failed `participation` query printed
   * "No guest has been invited yet", which is not a missing number but a
   * statement about the business.
   *
   * This became reachable only in this wave: before the settle() refactor a
   * failed aggregate took the whole endpoint down with it, so a page that
   * rendered at all had every section. Two of the four keys below (`books`,
   * `conversions`) also own a Retry card further down; `revenue` and
   * `participation` feed nothing but these sentences, so this is the only place
   * their failure can be admitted.
   */
  const subtitleFor = (key: string, text: () => string): string =>
    (failureFor(key) ? 'Could not be loaded.' : text());

  // TWO REASONS `kpis` CAN BE FALSY, AND THEY NEED OPPOSITE ANSWERS.
  //
  //   1. The backend PREDATES this key. The older payload still carries two
  //      all-time-ish 30-day numbers, and showing them beats four dashes.
  //   2. The backend HAS the key and the aggregate FAILED — a new state that
  //      settle() introduced in this wave.
  //
  // The legacy fallback cannot tell them apart on its own, and the numbers it
  // substitutes are always a 30-day window while the tile title says whatever
  // pill is selected. On `?range=7d` with a failed section that reads
  // "Revenue · the last 7 days" over a 30-day gross — a wrong number wearing a
  // confident label, which is worse than no number. `unavailable[]` is the
  // discriminator: only case 2 names the key.
  const kpisFailed = failureFor('kpis') !== null;
  const legacyGross30 = num(revenue?.gross_cents_30d);
  const legacyPrev30 = num(revenue?.gross_cents_prev_30d);
  const revenueKpi: KpiBlock =
    kpis?.revenue_cents
    || (revenue && !kpisFailed
      ? {
        value: revenue.gross_cents_30d,
        prev: revenue.gross_cents_prev_30d,
        delta_pct: legacyPrev30 > 0
          ? Math.round(((legacyGross30 - legacyPrev30) / legacyPrev30) * 1000) / 10
          : null,
      }
      : EMPTY_KPI);
  const booksKpi: KpiBlock =
    kpis?.books_created
    || (books && !kpisFailed ? { value: books.created_30d, prev: null, delta_pct: null } : EMPTY_KPI);
  const guestsKpi: KpiBlock = kpis?.guests_invited || EMPTY_KPI;
  const chaptersKpi: KpiBlock = kpis?.chapters_submitted || EMPTY_KPI;

  const liveBooks = (books?.active || 0) + (books?.collecting || 0);
  const participationPct = percentage(participation?.submitted, participation?.invited);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <WeddingIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Wedding Books
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <TimeRangeFilter
          value={range}
          onChange={setRange}
          onRefresh={load}
          refreshing={loading}
        />
      </Box>
      {/*
        The window, described by the SERVER — including its timezone note, which
        used to be printed by hand at the foot of this page. One caption, under
        the control that produced it, built from the same payload the numbers
        came from.
      */}
      <RangeMetaCaption meta={data?.range} sx={{ mb: 1 }} />
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Revenue, signups, invitations and template use across the whole portfolio, measured over{' '}
        {rangeLabel}.
      </Typography>

      {/*
        WRAPPED, like every other aggregate on this page.
        `kpis` is a settle() section and can fail on its own. Unwrapped, that
        failure rendered four em-dashes with no hint that a retry would fix
        them — indistinguishable from a portfolio that genuinely has no revenue,
        no books and no guests. The dash was honest; the silence was not.
      */}
      {section('kpis', 'Headline figures could not be loaded',
        <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WeddingKpiCard
            title={`Revenue · ${rangeLabel}`}
            value={
              revenueKpi.value === null || revenueKpi.value === undefined
                ? '—'
                : centsToUsdTile(revenueKpi.value)
            }
            subtitle={subtitleFor('revenue', () => `${centsToUsd(revenue?.gross_cents)} gross, all time`)}
            {...deltaFor(revenueKpi)}
            series={revenueKpi.series}
            tone="primary"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WeddingKpiCard
            title="New books"
            value={kpiCount(booksKpi)}
            subtitle={subtitleFor('books', () => `${books?.total || 0} books total · ${liveBooks} actively collecting`)}
            {...deltaFor(booksKpi)}
            series={booksKpi.series}
            tone="secondary"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WeddingKpiCard
            title="Guests invited"
            value={kpiCount(guestsKpi)}
            subtitle={subtitleFor('participation', () => (
              num(participation?.invited) > 0
                ? `${participationPct}% of all invited guests have written something`
                : 'No guest has been invited yet'
            ))}
            {...deltaFor(guestsKpi)}
            series={guestsKpi.series}
            tone="success"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WeddingKpiCard
            title="Chapters submitted"
            value={kpiCount(chaptersKpi)}
            subtitle={subtitleFor('conversions', () => `${conversions?.converted || 0} guests became LifeScribe members, all time`)}
            {...deltaFor(chaptersKpi)}
            series={chaptersKpi.series}
            tone="error"
            loading={loading}
          />
        </Grid>
        </Grid>
      )}

      {/*
        Gated on the PAYLOAD, not on `books`. `books` is one section among many
        and can now be null on its own; hanging the whole lower half of the page
        off it would mean one failed count hiding sixteen healthy ones.
      */}
      {!loading && data && (
        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12, lg: 7 }}>
            {section(
              'newest_couples',
              'Who is signing up could not be loaded',
              <NewestCouplesCard
                rows={data.newest_couples ?? undefined}
                acquisitionAvailable={data.acquisition_source_available}
                acquisitionReason={data.acquisition_source_reason}
                rangeLabel={rangeLabel}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            {section(
              'invitation_funnel',
              'The invitation funnel could not be loaded',
              <InvitationFunnelCard
                funnel={data.invitation_funnel ?? undefined}
                rangeLabel={rangeLabel}
              />
            )}
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            {section(
              'template_usage',
              'Template usage could not be loaded',
              <TemplateUsageCard usage={data.template_usage ?? undefined} rangeLabel={rangeLabel} />
            )}
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            {section(
              'email_templates_summary',
              'Email template statistics could not be loaded',
              <EmailTemplatesSummaryCard
                summary={data.email_templates_summary ?? undefined}
                rangeLabel={rangeLabel}
              />
            )}
          </Grid>

          <Grid size={{ xs: 12, lg: 8 }}>
            {section(
              'books',
              'Books by status could not be loaded',
              books ? <StatusFunnelCard books={books} /> : null
            )}
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Stack spacing={3}>
              {section(
                'reminder_pressure',
                'Reminder pressure could not be loaded',
                <ReminderPressureCard pressure={data.reminder_pressure} />
              )}
              {section(
                'voice_quota',
                'The voice quota could not be loaded',
                <VoiceQuotaCard quota={data.voice_quota} />
              )}
              {section(
                'conversions',
                'Guest conversions could not be loaded',
                <ConversionFunnelCard funnel={conversions || EMPTY_FUNNEL} />
              )}
              {section(
                'engagement',
                'Invitation email health could not be loaded',
                <Card sx={{ borderRadius: '16px' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Invitation email health
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Invitations and reminders only — OTPs and receipts are excluded. All time.
                    </Typography>
                    {/*
                      WB-13. TWO OF THESE FOUR NUMBERS ARE MEASURED AND TWO ARE NOT,
                      AND THE CHIPS NOW SAY WHICH.

                      `clicked_at` became real when the /g/{code} gateway started
                      stamping it (session BB); before that this card showed a
                      permanent 0% clicked and nobody could tell whether the
                      campaign was failing or the column was empty.

                      `opened_at` and `bounced_at` are NO LONGER structurally
                      empty, but whether they are populated is a property of the
                      DATABASE, not something this file can know. Brevo's
                      webhooks 404 in this AWS account, so the wedding_stitch job
                      in lifescribe-email reconciles provider events onto
                      invitation rows every 15 minutes instead — the same pull
                      approach the survey system had to take.

                      So the server decides, and says why: it reads that job's
                      own sync state and ships `opened_tracked` / `bounced_tracked`
                      with a `tracking_reason` sentence. These chips render its
                      answer. That is what lets the card start telling the truth
                      the moment the job first succeeds, with no admin deploy —
                      and hardcoding "not tracked" here is what kept the whole
                      reconciliation invisible after the job had already shipped.
                    */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`${engagement?.sent || 0} sent`} size="small" />
                      <Tooltip title="Invitation links opened at least once, stamped by the gateway page. Counts every load, including the link-scanners some corporate mail systems run — an upper bound on human interest.">
                        <Chip
                          label={`${percentage(engagement?.clicked, engagement?.sent)}% clicked`}
                          size="small"
                          color="success"
                        />
                      </Tooltip>
                      {/* `?? false` and not `?? true`: a bundle talking to a
                          backend that predates these flags must fall back to
                          "not tracked", never to a confident 0%. */}
                      <Tooltip title={trackingHelp(engagement)}>
                        {engagement?.opened_tracked ?? false ? (
                          <Chip
                            label={`${percentage(engagement?.opened, engagement?.sent)}% opened`}
                            size="small"
                            color="info"
                          />
                        ) : (
                          <Chip label="opens not tracked" size="small" variant="outlined" />
                        )}
                      </Tooltip>
                      <Tooltip title={trackingHelp(engagement)}>
                        {engagement?.bounced_tracked ?? false ? (
                          <Chip
                            label={`${engagement?.bounced || 0} bounced`}
                            size="small"
                            variant={engagement?.bounced ? 'filled' : 'outlined'}
                            color={engagement?.bounced ? 'error' : 'default'}
                          />
                        ) : (
                          <Chip
                            label={engagement?.bounced ? `${engagement.bounced} bounced` : 'bounces not tracked'}
                            size="small"
                            variant={engagement?.bounced ? 'filled' : 'outlined'}
                            color={engagement?.bounced ? 'error' : 'default'}
                          />
                        )}
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            {section(
              'writing_quality',
              'Writing quality could not be loaded',
              <WritingQualityCard quality={data.writing_quality} rangeLabel={rangeLabel} />
            )}
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            {section(
              'anniversary',
              'The anniversary programme could not be loaded',
              <AnniversaryProgrammeCard anniversary={data.anniversary} />
            )}
          </Grid>

          <Grid size={12}>
            {section(
              'recent_activity',
              'The activity feed could not be loaded',
              <RecentActivityCard seed={data.recent_activity} />
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default WeddingOverviewPage;
